/**
 * Service 3D pour Module Structure de Base
 * 
 * Gestion de la géométrie Three.js avec isolation totale
 * Performance optimisée selon cahier des charges
 */

import * as THREE from 'three';
import {
  GeometrieForme,
  Poutre,
  Montant,
  StructureBase,
  Point3D,
  TypeMateriauGlobal
} from '../types';

/**
 * Service de génération 3D pour la structure de base
 * Isolation complète - aucun impact sur autres modules
 */
export class StructureBase3DService {
  private scene: THREE.Scene;
  private group: THREE.Group;
  private materials: { [key: string]: THREE.Material };

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'structure-base';
    // Ne pas ajouter automatiquement à la scène - sera géré par le composant
    
    // Matériaux optimisés
    this.materials = {
      poutre: new THREE.MeshLambertMaterial({ color: 0x8B4513 }), // Brun bois
      montant: new THREE.MeshLambertMaterial({ color: 0x654321 }), // Brun foncé
      forme: new THREE.LineBasicMaterial({ color: 0x00ff00, linewidth: 2 }) // Vert debug
    };
  }

  /**
   * Génération complète de la structure 3D
   */
  genererStructure3D(structure: StructureBase, materiau: TypeMateriauGlobal = TypeMateriauGlobal.FER_A_BETON, epaisseur: number = 2.0): THREE.Group {
    try {
  
      
      // Nettoyage précédent
      this.clearStructure();

      if (structure.error) {
        console.warn('[StructureBase3D] Structure avec erreur:', structure.error);
        return this.group;
      }

      // Génération des éléments
  
      this.genererForme3D(structure.forme);
      
  
      this.genererPoutres3D(structure.poutres, materiau, epaisseur);
      
  
      this.genererMontants3D(structure.montants, materiau, epaisseur);

  
      return this.group;

    } catch (error) {
      console.error('[StructureBase3D] Erreur génération 3D:', error);
      return this.group;
    }
  }

  /**
   * Génération de la forme de base (contour)
   */
  private genererForme3D(forme: GeometrieForme): void {
    if (!forme.points || forme.points.length === 0) return;

    try {
      const points = forme.points.map(p => new THREE.Vector3(p.x, p.y, p.z));
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, this.materials.forme);
      line.name = 'forme-contour';
      this.group.add(line);

    } catch (error) {
      console.error('[StructureBase3D] Erreur génération forme:', error);
    }
  }

  /**
   * Génération des poutres haute et basse
   */
  private genererPoutres3D(poutres: { poutreBasse: Poutre; poutreHaute: Poutre }, materiau: TypeMateriauGlobal, epaisseur: number): void {
    try {
      this.genererPoutre3D(poutres.poutreBasse, 'poutre-basse', materiau, epaisseur);
      this.genererPoutre3D(poutres.poutreHaute, 'poutre-haute', materiau, epaisseur);
    } catch (error) {
      console.error('[StructureBase3D] Erreur génération poutres:', error);
    }
  }

  /**
   * Génération d'une poutre individuelle
   */
  private genererPoutre3D(poutre: Poutre, nom: string, materiau: TypeMateriauGlobal, epaisseur: number): void {
    if (!poutre.chemin || poutre.chemin.length === 0) return;

    try {
      // Création de segments droits entre chaque point pour respecter la forme exacte
      const groupePoutre = new THREE.Group();
      groupePoutre.name = nom;
      
      for (let i = 0; i < poutre.chemin.length - 1; i++) {
        const pointA = poutre.chemin[i];
        const pointB = poutre.chemin[i + 1];
        
        // Calcul de la distance et direction entre les points
        // Les points du chemin ont déjà la bonne coordonnée Y (0 pour basse, hauteur pour haute)
        const vecA = new THREE.Vector3(pointA.x, pointA.y, pointA.z);
        const vecB = new THREE.Vector3(pointB.x, pointB.y, pointB.z);
        const direction = new THREE.Vector3().subVectors(vecB, vecA);
        const longueur = direction.length();
        
        // Création de la géométrie selon le matériau
      // Conversion de l'épaisseur de mm en mètres
      const epaisseurMetres = epaisseur / 1000;
        let geometry: THREE.BufferGeometry;
        if (materiau === TypeMateriauGlobal.FER_A_BETON) {
          // Forme cylindrique pour fer à béton
          geometry = new THREE.CylinderGeometry(
            epaisseurMetres / 2, // Rayon haut
            epaisseurMetres / 2, // Rayon bas
            longueur, // Hauteur (longueur du segment)
            8 // Segments radiaux
          );
        } else {
          // Forme carrée pour tube carré
          geometry = new THREE.BoxGeometry(
            epaisseurMetres, // Largeur
            longueur, // Hauteur (longueur du segment)
            epaisseurMetres // Profondeur
          );
        }
        
        const mesh = new THREE.Mesh(geometry, this.materials.poutre);
        
        // Positionnement au centre du segment
        const centre = new THREE.Vector3().addVectors(vecA, vecB).multiplyScalar(0.5);
        mesh.position.copy(centre);
        
        // Orientation du cylindre selon la direction du segment
        const axeY = new THREE.Vector3(0, 1, 0);
        direction.normalize();
        const quaternion = new THREE.Quaternion().setFromUnitVectors(axeY, direction);
        mesh.setRotationFromQuaternion(quaternion);
        
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.name = `${nom}-segment-${i}`;
        
        groupePoutre.add(mesh);
      }
      
      this.group.add(groupePoutre);

    } catch (error) {
      console.error(`[StructureBase3D] Erreur génération poutre ${nom}:`, error);
    }
  }

  /**
   * Génération des montants verticaux
   */
  private genererMontants3D(montants: Montant[], materiau: TypeMateriauGlobal, epaisseur: number): void {

    
    if (!montants || montants.length === 0) {
      console.warn('[StructureBase3D] Aucun montant à générer');
      return;
    }

    try {
      montants.forEach((montant, index) => {
  
        this.genererMontant3D(montant, `montant-${index}`, materiau, epaisseur);
      });
  
    } catch (error) {
      console.error('[StructureBase3D] Erreur génération montants:', error);
    }
  }

  /**
   * Coupe les montants de base sous les ouvertures de portes et conserve uniquement la partie supérieure.
   * Chaque zone de porte est définie par son emprise au sol (minX/maxX/minZ/maxZ) et la hauteur supérieure yMax.
   */
  public couperMontantsSousPortes(
    zones: { minX: number; maxX: number; minZ: number; maxZ: number; yMax: number }[],
    materiau: TypeMateriauGlobal,
    epaisseur: number
  ): void {
    if (!zones || zones.length === 0) return;

    const tol = 1e-4;
    const epaisseurMetres = epaisseur / 1000;

    // Récupérer tous les montants (meshes nommés "montant-*")
    const aSupprimer: THREE.Object3D[] = [];
    this.group.children.forEach((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh || !(mesh as any).isMesh) return;
      if (!mesh.name || !mesh.name.startsWith('montant-')) return;

      // Position XZ du centre du montant
      const cx = mesh.position.x;
      const cz = mesh.position.z;

      // Déterminer si le montant tombe dans l'emprise horizontale d'au moins une porte
      const zonesCouvrantes = zones.filter(z => (
        cx >= z.minX - tol && cx <= z.maxX + tol &&
        cz >= z.minZ - tol && cz <= z.maxZ + tol
      ));

      if (zonesCouvrantes.length === 0) return; // aucune découpe à faire pour ce montant

      // Calculer la coupe verticale: on garde uniquement au-dessus du max des yMax des portes couvrantes
      const yCut = Math.max(...zonesCouvrantes.map(z => z.yMax));

      // Calculer hauteur totale et top/bottom actuels via bounding box
      const bbox = new THREE.Box3().setFromObject(mesh);
      const bottomY = bbox.min.y;
      const topY = bbox.max.y;

      // Si la porte monte jusqu'au sommet (ou presque), supprimer ce montant
      if (yCut >= topY - tol) {
        aSupprimer.push(mesh);
        return;
      }

      const nouvelleHauteur = Math.max(0, topY - yCut);
      if (nouvelleHauteur <= tol) {
        aSupprimer.push(mesh);
        return;
      }

      // Recréer une géométrie du même type (cylindre ou boîte) mais limitée à la partie au-dessus de la porte
      let newGeom: THREE.BufferGeometry;
      if (materiau === TypeMateriauGlobal.FER_A_BETON) {
        newGeom = new THREE.CylinderGeometry(epaisseurMetres / 2, epaisseurMetres / 2, nouvelleHauteur, 8);
      } else {
        newGeom = new THREE.BoxGeometry(epaisseurMetres, nouvelleHauteur, epaisseurMetres);
      }

      // Positionner le nouveau segment au bon centre vertical
      const newCenterY = yCut + (nouvelleHauteur / 2);

      // Remplacer la géométrie et repositionner en Y
      if (mesh.geometry) mesh.geometry.dispose();
      mesh.geometry = newGeom;
      mesh.position.set(cx, newCenterY, cz);
    });

    // Supprimer les montants qui n'ont plus de partie conservée
    if (aSupprimer.length > 0) {
      aSupprimer.forEach(obj => {
        if ((obj as THREE.Mesh).geometry) {
          (obj as THREE.Mesh).geometry.dispose();
        }
        this.group.remove(obj);
      });
    }
  }

  /**
   * Génération d'un montant individuel
   */
  private genererMontant3D(montant: Montant, nom: string, materiau: TypeMateriauGlobal, epaisseur: number): void {
    try {
      
      // Création de la géométrie selon le matériau
      // Conversion de l'épaisseur de mm en mètres
      const epaisseurMetres = epaisseur / 1000;
      let geometry: THREE.BufferGeometry;
      if (materiau === TypeMateriauGlobal.FER_A_BETON) {
        // Forme cylindrique pour fer à béton
        geometry = new THREE.CylinderGeometry(
          epaisseurMetres / 2, // Rayon haut
          epaisseurMetres / 2, // Rayon bas
          montant.hauteur, // Hauteur
          8 // Segments radiaux
        );
      } else {
        // Forme carrée pour tube carré
        geometry = new THREE.BoxGeometry(
          epaisseurMetres, // Largeur
          montant.hauteur, // Hauteur
          epaisseurMetres // Profondeur
        );
      }
      
      const mesh = new THREE.Mesh(geometry, this.materials.montant);
      
      // Positionnement
      mesh.position.set(
        montant.position.x,
        montant.position.y + montant.hauteur / 2, // Centre vertical
        montant.position.z
      );
      
      mesh.name = nom;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      
  
      this.group.add(mesh);
  

    } catch (error) {
      console.error(`[StructureBase3D] Erreur génération montant ${nom}:`, error);
    }
  }

  /**
   * Mise à jour en temps réel des paramètres
   */
  updateStructure(structure: StructureBase): void {
    try {
      this.genererStructure3D(structure);
    } catch (error) {
      console.error('[StructureBase3D] Erreur mise à jour:', error);
    }
  }

  /**
   * Met à jour la couleur de tous les matériaux de la structure
   */
  mettreAJourCouleur(couleur: string): void {
    try {
      const color = new THREE.Color(couleur);
      
      // Mise à jour des matériaux des poutres et montants
      (this.materials.poutre as THREE.MeshLambertMaterial).color = color;
      (this.materials.montant as THREE.MeshLambertMaterial).color = color;
      
  
    } catch (error) {
      console.error('[StructureBase3D] Erreur mise à jour couleur:', error);
    }
  }

  /**
   * Nettoyage de la structure
   */
  clearStructure(): void {
    try {
      // Suppression de tous les enfants
      while (this.group.children.length > 0) {
        const child = this.group.children[0];
        this.group.remove(child);
        
        // Nettoyage de la géométrie et des matériaux
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
        } else if (child instanceof THREE.Line) {
          if (child.geometry) child.geometry.dispose();
        }
      }
    } catch (error) {
      console.error('[StructureBase3D] Erreur nettoyage:', error);
    }
  }

  /**
   * Obtenir le groupe 3D
   */
  getGroup(): THREE.Group {
    return this.group;
  }

  /**
   * Calcul des dimensions pour centrage caméra
   */
  getBoundingBox(): THREE.Box3 {
    try {
      const box = new THREE.Box3();
      box.setFromObject(this.group);
      return box;
    } catch (error) {
      console.error('[StructureBase3D] Erreur calcul bounding box:', error);
      return new THREE.Box3();
    }
  }

  /**
   * Nettoyage complet du service
   */
  dispose(): void {
    try {
      this.clearStructure();
      // Le groupe sera retiré par le composant qui l'a ajouté
      
      // Nettoyage des matériaux
      Object.values(this.materials).forEach(material => {
        if (material instanceof THREE.Material) {
          material.dispose();
        }
      });
      
    } catch (error) {
      console.error('[StructureBase3D] Erreur dispose:', error);
    }
  }
}

/**
 * Utilitaires de conversion
 */
export class GeometryUtils {
  /**
   * Conversion Point3D vers Vector3
   */
  static pointToVector3(point: Point3D): THREE.Vector3 {
    return new THREE.Vector3(point.x, point.y, point.z);
  }

  /**
   * Conversion Vector3 vers Point3D
   */
  static vector3ToPoint(vector: THREE.Vector3): Point3D {
    return { x: vector.x, y: vector.y, z: vector.z };
  }

  /**
   * Calcul du centre d'une forme
   */
  static calculerCentre(points: Point3D[]): Point3D {
    if (points.length === 0) return { x: 0, y: 0, z: 0 };
    
    const sum = points.reduce(
      (acc, point) => ({
        x: acc.x + point.x,
        y: acc.y + point.y,
        z: acc.z + point.z
      }),
      { x: 0, y: 0, z: 0 }
    );
    
    return {
      x: sum.x / points.length,
      y: sum.y / points.length,
      z: sum.z / points.length
    };
  }
}